import { css } from 'styled-components';

/**
 * The design system's Form Fields state pattern (docs/design-system.md §
 * "Form Fields (Bordered Primitives)"), as shared snippets.
 *
 * Lives in `common/` because it is pure UI with no domain knowledge and every
 * feature needs it. It used to be declared inside
 * `transcript/translationEditorPrimitives.tsx`, which is why unrelated
 * surfaces (comments, Content Studio) had to reach across into `transcript/`
 * for it — or, more often, hand-rolled their own and drifted.
 */

/**
 * Standard focused-field treatment for text-entry fields: neutral gray border
 * plus a 1px matching ring, via the dedicated `--focus-border` / `--focus-ring`
 * tokens. Green (`--primary`) is reserved for action buttons and gold
 * (`--accent`) for visual/creative controls — neither means "you are typing
 * here" (PLAT-10644).
 */
export const fieldFocusCss = css`
  &:focus {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

/**
 * The whole resting → hover → focus escalation for a bordered field, driven by
 * `border-color` alone so field chrome stays invisible until the user signals
 * intent. Hosts supply their own box (padding, radius, type scale) and fill;
 * this supplies only the states.
 */
export const fieldStateCss = css`
  border: 1px solid var(--border);
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
  }

  ${fieldFocusCss}
`;
